/**
 * PostCard.jsx — ELITE PRODUCTION v5.0 "Portal Edition"
 * Place at: src/components/PostCard.jsx
 *
 * KEY FIX vs v4:
 *   The three-dot menu now renders via ReactDOM.createPortal directly onto
 *   document.body. This escapes ALL parent stacking contexts (transform,
 *   overflow:hidden, will-change) which trapped position:fixed on iOS Safari.
 *
 *   On mobile (≤767px): renders a beautiful bottom action sheet.
 *   On desktop (≥768px): renders the original glassmorphism dropdown.
 *
 * ALL FUNCTIONALITY PRESERVED:
 *   Vote, bookmark, report, follow/unfollow, delete, copy link,
 *   double-tap to like, comment section, moderator extra actions,
 *   @mention navigation, pinned badge, you-badge.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { supabase } from '../supabase';
import { formatTimeAgo, TAGS } from '../utils/moderation';
import CommentSection from './CommentSection';

const REPORT_REASONS = [
  'Inappropriate content',
  'Harassment or bullying',
  'Spam',
  'Misinformation',
  'Hate speech',
  'Other',
];

const DOUBLE_TAP_DELAY = 250;

/* ─── CSS ─────────────────────────────────────────────────────────────────── */
const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

  :root {
    --pc-spring: cubic-bezier(0.25, 1, 0.4, 1);
    --pc-bounce: cubic-bezier(0.34, 1.56, 0.64, 1);
  }

  /* ── Core Card ── */
  .pc-card {
    background: var(--card, #FFFFFF);
    border-radius: 24px;
    border: 1px solid var(--sand-light, #F0F0F0);
    margin-bottom: 16px;
    position: relative;
    font-family: 'Plus Jakarta Sans', -apple-system, sans-serif;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.03);
    transition: transform 0.2s var(--pc-spring), box-shadow 0.2s var(--pc-spring);
    overflow: visible;
  }
  .pc-card:active { transform: scale(0.99); }
  body.dark-theme .pc-card {
    background: var(--card, #1e2230);
    border-color: rgba(255,255,255,0.05);
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  }

  /* ── Header ── */
  .pc-header {
    display: flex; align-items: center; gap: 12px;
    padding: 20px 20px 12px;
  }
  .pc-avatar {
    width: 44px; height: 44px; border-radius: 50%;
    display: flex; align-items: center; justify-content: center;
    font-weight: 800; font-size: 1.1rem; flex-shrink: 0;
    cursor: pointer; user-select: none;
    transition: transform 0.2s var(--pc-bounce);
    box-shadow: 0 4px 12px rgba(0,0,0,0.06);
    border: 2px solid rgba(255,255,255,0.4);
  }
  .pc-avatar:active { transform: scale(0.9); }
  .pc-user-info { flex: 1; min-width: 0; display: flex; flex-direction: column; justify-content: center; }
  .pc-username-row { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
  .pc-username {
    font-size: 1rem; font-weight: 800; color: var(--ink, #111);
    cursor: pointer; line-height: 1.2; letter-spacing: -0.01em;
  }
  .pc-you-badge {
    font-size: 0.65rem; font-weight: 800; text-transform: uppercase;
    background: var(--brand-wash, rgba(90,105,147,0.1));
    color: var(--brand, #5A6993); padding: 3px 8px; border-radius: 99px;
  }
  .pc-meta { font-size: 0.8rem; color: var(--muted, #6B7280); font-weight: 500; margin-top: 2px; }

  .pc-more-btn {
    width: 36px; height: 36px; border-radius: 50%;
    border: none; background: transparent; cursor: pointer;
    color: var(--muted, #6B7280); font-size: 1.2rem; font-weight: 800;
    display: flex; align-items: center; justify-content: center;
    transition: all 0.2s; flex-shrink: 0;
    -webkit-tap-highlight-color: transparent;
  }
  .pc-more-btn:active { background: var(--cloud, #F3F4F6); transform: scale(0.9); color: var(--ink, #111); }

  /* ── Body & Double Tap ── */
  .pc-body-container {
    padding: 0 20px 16px; position: relative;
    cursor: pointer; -webkit-tap-highlight-color: transparent;
  }
  .pc-body {
    font-size: 0.98rem; line-height: 1.6; color: var(--ink, #111);
    margin: 0; white-space: pre-wrap; word-break: break-word;
    font-weight: 500; letter-spacing: -0.01em;
  }

  /* ── Big Heart ── */
  .pc-big-heart {
    position: absolute; top: 50%; left: 50%;
    transform: translate(-50%, -50%) scale(0);
    color: #EF4444; opacity: 0; pointer-events: none; z-index: 10;
    filter: drop-shadow(0 10px 20px rgba(239,68,68,0.4));
  }
  .pc-big-heart.animate { animation: bigHeartPop 0.9s var(--pc-bounce) forwards; }
  @keyframes bigHeartPop {
    0%  { transform: translate(-50%,-50%) scale(0); opacity: 0; }
    15% { transform: translate(-50%,-50%) scale(1.3); opacity: 0.9; }
    30% { transform: translate(-50%,-50%) scale(1); opacity: 1; }
    70% { transform: translate(-50%,-50%) scale(1); opacity: 1; }
    100%{ transform: translate(-50%,-50%) scale(0.8); opacity: 0; }
  }

  /* ── Tag ── */
  .pc-tag {
    display: inline-flex; align-items: center; gap: 6px;
    padding: 4px 12px; border-radius: 99px;
    font-size: 0.75rem; font-weight: 800; letter-spacing: 0.02em;
    margin: 0 20px 16px; width: max-content;
  }

  /* ── Action Bar ── */
  .pc-actions {
    display: flex; align-items: center; gap: 8px; flex-wrap: wrap;
    padding: 0 20px 20px;
  }
  .pc-pill {
    display: inline-flex; align-items: center; gap: 6px;
    padding: 8px 16px; border-radius: 99px; border: none;
    font-size: 0.85rem; font-weight: 700; cursor: pointer;
    background: var(--cloud, #F4F7F9); color: var(--muted, #6B7280);
    transition: all 0.2s var(--pc-spring);
    -webkit-tap-highlight-color: transparent; user-select: none;
    font-family: inherit;
  }
  body.dark-theme .pc-pill { background: rgba(255,255,255,0.05); color: var(--muted); }
  .pc-pill:active:not(:disabled) { transform: scale(0.92); }
  .pc-pill.liked { background: rgba(239,68,68,0.1); color: #EF4444; }
  .pc-pill.saved { background: rgba(16,185,129,0.1); color: #10B981; }
  .pc-pill.comments-active { background: var(--brand-wash,rgba(90,105,147,0.1)); color: var(--brand,#5A6993); }
  .pc-pill:disabled { opacity: 0.5; cursor: not-allowed; transform: none; }
  .pc-pinned {
    font-size: 0.75rem; font-weight: 800; color: #F59E0B;
    background: rgba(245,158,11,0.1); padding: 4px 10px; border-radius: 99px;
    margin-left: auto; display: flex; align-items: center; gap: 4px;
  }

  /* ── Heart micro-animation ── */
  .pc-heart-icon { transition: transform 0.3s var(--pc-bounce); }
  .pc-pill.liked .pc-heart-icon { animation: smallHeartPop 0.4s var(--pc-bounce); }
  @keyframes smallHeartPop {
    0%   { transform: scale(1); }
    40%  { transform: scale(1.4); }
    100% { transform: scale(1); }
  }

  /* ── DESKTOP dropdown (position:fixed, portal-rendered) ── */
  .pc-menu-overlay {
    position: fixed; inset: 0; z-index: 99998;
    -webkit-tap-highlight-color: transparent;
  }
  .pc-menu {
    position: fixed; z-index: 99999;
    background: rgba(255,255,255,0.88);
    backdrop-filter: blur(20px); -webkit-backdrop-filter: blur(20px);
    border: 1px solid var(--sand-light, #F0F0F0);
    border-radius: 20px;
    box-shadow: 0 16px 40px rgba(0,0,0,0.14);
    min-width: 240px; overflow: hidden; padding: 8px 0;
    animation: pcMenuIn 0.2s var(--pc-spring);
    transform-origin: top right;
  }
  body.dark-theme .pc-menu { background: rgba(30,34,48,0.9); border-color: rgba(255,255,255,0.1); }
  @keyframes pcMenuIn {
    from { opacity: 0; transform: scale(0.92) translateY(-8px); }
    to   { opacity: 1; transform: scale(1) translateY(0); }
  }

  /* ── MOBILE bottom action sheet (portal-rendered) ── */
  .pc-sheet-overlay {
    position: fixed; inset: 0; z-index: 99998;
    background: rgba(0,0,0,0.48);
    backdrop-filter: blur(5px); -webkit-backdrop-filter: blur(5px);
    animation: pcFadeIn .18s ease;
    -webkit-tap-highlight-color: transparent;
  }
  @keyframes pcFadeIn { from{opacity:0} to{opacity:1} }

  .pc-action-sheet {
    position: fixed;
    bottom: 0; left: 0; right: 0;
    z-index: 99999;
    background: var(--card, #fff);
    border-radius: 24px 24px 0 0;
    padding-bottom: calc(env(safe-area-inset-bottom, 0px) + 12px);
    box-shadow: 0 -8px 40px rgba(0,0,0,0.18);
    animation: pcSheetUp .28s cubic-bezier(.25,1,.4,1);
    overflow: hidden;
  }
  body.dark-theme .pc-action-sheet { background: var(--card, #1e2230); }
  @keyframes pcSheetUp {
    from { transform: translateY(100%); }
    to   { transform: translateY(0); }
  }

  .pc-sheet-handle {
    width: 36px; height: 4px; border-radius: 99px;
    background: var(--sand, #D6D2C4);
    margin: 12px auto 4px;
  }

  /* Author info banner inside sheet */
  .pc-sheet-author {
    display: flex; align-items: center; gap: 10px;
    padding: 12px 20px 10px;
    border-bottom: 1px solid var(--sand-light, #F0F0F0);
    margin-bottom: 4px;
  }
  .pc-sheet-author-avatar {
    width: 38px; height: 38px; border-radius: 50%; flex-shrink: 0;
    display: flex; align-items: center; justify-content: center;
    font-weight: 800; font-size: 1rem;
  }
  .pc-sheet-author-name {
    font-size: .9rem; font-weight: 700; color: var(--ink, #111);
  }
  .pc-sheet-author-time {
    font-size: .72rem; color: var(--muted, #6B7280); margin-top: 1px;
  }

  /* Shared menu item styles */
  .pc-menu-item {
    display: flex; align-items: center; gap: 12px;
    width: 100%; padding: 14px 20px;
    background: none; border: none; text-align: left;
    font-size: 0.95rem; font-weight: 600; cursor: pointer;
    color: var(--ink, #111); transition: background 0.15s;
    font-family: inherit;
  }
  .pc-menu-item:active { background: var(--cloud, #F4F7F9); }
  .pc-menu-item.danger { color: #EF4444; }
  .pc-menu-item.danger:active { background: rgba(239,68,68,0.1); }
  .pc-menu-sep { height: 1px; background: var(--sand-light, #F0F0F0); margin: 6px 0; }
  .pc-menu-label {
    padding: 10px 20px 4px; font-size: 0.7rem; font-weight: 800;
    color: var(--muted, #6B7280); text-transform: uppercase; letter-spacing: 0.08em;
  }

  /* ── Cancel button (sheet only) ── */
  .pc-sheet-cancel {
    display: block; width: calc(100% - 28px); margin: 8px 14px 0;
    padding: 14px 0; border-radius: 14px;
    background: var(--cloud, #F4F7F9); border: none;
    font-family: inherit; font-size: .95rem; font-weight: 700;
    color: var(--ink, #111); cursor: pointer;
    -webkit-tap-highlight-color: transparent;
    transition: background .15s;
  }
  .pc-sheet-cancel:active { background: var(--sand-light, #EBE7DF); }
  body.dark-theme .pc-sheet-cancel { background: rgba(255,255,255,0.08); color: var(--ink,#e0ddd8); }

  /* ── Comments Section ── */
  .pc-comments-shell {
    border-top: 1px solid var(--sand-light, #F0F0F0);
    background: var(--bg, #F9F4F0);
    padding: 20px; border-radius: 0 0 24px 24px;
    animation: commentsSlideDown 0.3s var(--pc-spring);
  }
  body.dark-theme .pc-comments-shell {
    background: var(--cloud, #151820);
    border-color: rgba(255,255,255,0.05);
  }
  @keyframes commentsSlideDown {
    from { opacity: 0; transform: translateY(-10px); }
    to   { opacity: 1; transform: translateY(0); }
  }
`;

/* ── CSS injection (once) ────────────────────────────────────────────────── */
let injected = false;
function injectCSS() {
  if (injected || typeof document === 'undefined') return;
  injected = true;
  const el = document.createElement('style');
  el.textContent = CSS;
  document.head.appendChild(el);
}

/* ── Avatar palette ──────────────────────────────────────────────────────── */
const AVATAR_PALETTE = [
  { bg: '#FEF3C7', color: '#D97706' },
  { bg: '#EDE9FE', color: '#7C3AED' },
  { bg: '#DBEAFE', color: '#2563EB' },
  { bg: '#D1FAE5', color: '#059669' },
  { bg: '#FCE7F3', color: '#BE185D' },
  { bg: '#FDE8DF', color: '#C1440E' },
  { bg: '#E0F2FE', color: '#0284C7' },
  { bg: '#F3F4F6', color: '#374151' },
];
function getAvatarStyle(username) {
  const idx = (username?.charCodeAt(0) || 0) % AVATAR_PALETTE.length;
  return AVATAR_PALETTE[idx];
}

/* ─────────────────────────────────────────────────────────────────────────
   MAIN COMPONENT
──────────────────────────────────────────────────────────────────────────── */
const PostCard = ({
  post,
  currentUser,
  onDelete,
  onNavigateToProfile,
  onVoteUpdate,
  extraActions = [],
  stateProvided    = false,
  initialVote      = null,
  initialBookmarked = false,
  initialReported  = false,
}) => {
  injectCSS();

  /* ── State ─────────────────────────────────────────────────────────── */
  const [userVote,      setUserVote]      = useState(initialVote);
  const [upvotes,       setUpvotes]       = useState(post.upvotes || 0);
  const [bookmarked,    setBookmarked]    = useState(initialBookmarked);
  const [reported,      setReported]      = useState(initialReported);
  const [following,     setFollowing]     = useState(false);
  const [followLoading, setFollowLoading] = useState(false);
  const [showComments,  setShowComments]  = useState(false);
  const [showMenu,      setShowMenu]      = useState(false);
  const [menuStep,      setMenuStep]      = useState('main');
  const [copied,        setCopied]        = useState(false);
  const [commentCount,  setCommentCount]  = useState(post.comment_count || 0);
  const [deleting,      setDeleting]      = useState(false);
  const [voteLoading,   setVoteLoading]   = useState(false);
  const [bigHeartAnim,  setBigHeartAnim]  = useState(false);

  /* Desktop dropdown position */
  const [menuPos, setMenuPos] = useState({ top: 0, right: 16 });

  /* Detect mobile once */
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    setIsMobile(typeof window !== 'undefined' && window.innerWidth < 768);
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const menuBtnRef = useRef(null);
  const lastTap    = useRef(0);

  const isOwner = currentUser?.id === post.author_id;
  const isLiked = userVote === 'up';
  const tagInfo = TAGS.find(t => t.label === post.tag) || TAGS[0];
  const { bg: avatarBg, color: avatarColor } = getAvatarStyle(post.author_username);

  /* ── Load user state ─────────────────────────────────────────────── */
  useEffect(() => {
    if (!currentUser) return;
    if (stateProvided) {
      if (!isOwner) {
        supabase.from('follows')
          .select('following_id')
          .eq('follower_id', currentUser.id)
          .eq('following_id', post.author_id)
          .maybeSingle()
          .then(({ data }) => { if (data) setFollowing(true); });
      }
      return;
    }
    const queries = [
      supabase.from('post_votes').select('vote_type').eq('user_id', currentUser.id).eq('post_id', post.id).maybeSingle(),
      supabase.from('bookmarks').select('post_id').eq('user_id', currentUser.id).eq('post_id', post.id).maybeSingle(),
      supabase.from('reports').select('post_id').eq('reporter_id', currentUser.id).eq('post_id', post.id).maybeSingle(),
    ];
    if (!isOwner) queries.push(
      supabase.from('follows').select('following_id').eq('follower_id', currentUser.id).eq('following_id', post.author_id).maybeSingle()
    );
    Promise.all(queries).then(([{ data: vote }, { data: bm }, { data: rp }, fRes]) => {
      if (vote) setUserVote(vote.vote_type);
      setBookmarked(!!bm);
      setReported(!!rp);
      if (fRes) setFollowing(!!fRes.data);
    });
  }, [currentUser, post.id, post.author_id, isOwner, stateProvided]);

  /* ── Vote ────────────────────────────────────────────────────────── */
  const handleVote = useCallback(async () => {
    if (!currentUser || voteLoading) return;
    setVoteLoading(true);
    const current  = userVote;
    const isToggle = current === 'up';
    const newVote  = isToggle ? null : 'up';
    setUserVote(newVote);
    setUpvotes(v => Math.max(0, v + (isToggle ? -1 : 1)));
    if (!isToggle && navigator.vibrate) navigator.vibrate(10);
    try {
      const { error } = await supabase.rpc('atomic_vote', {
        p_post_id: post.id, p_user_id: currentUser.id,
        p_vote_type: isToggle ? 'remove' : 'up',
        p_previous_vote: isToggle ? 'up' : null,
      });
      if (error) throw error;
      if (!isToggle && post.author_id !== currentUser.id) {
        supabase.from('notifications').upsert({
          recipient_id: post.author_id, type: 'upvote',
          actor_username: currentUser.username, post_id: post.id,
          message: `${currentUser.username} liked your post`, is_read: false,
        }, { onConflict: 'recipient_id,type,actor_username,post_id', ignoreDuplicates: true }).then(() => {});
      }
    } catch (_) {
      setUserVote(current);
      setUpvotes(v => Math.max(0, v - (isToggle ? -1 : 1)));
    } finally { setVoteLoading(false); }
  }, [currentUser, post.id, post.author_id, userVote, voteLoading]);

  /* ── Double tap ──────────────────────────────────────────────────── */
  const handleDoubleTap = (e) => {
    if (e.target.closest('.pc-pill')) return;
    const now = Date.now();
    if (now - lastTap.current < DOUBLE_TAP_DELAY) {
      if (navigator.vibrate) navigator.vibrate(10);
      setBigHeartAnim(true);
      setTimeout(() => setBigHeartAnim(false), 900);
      if (!isLiked) handleVote();
    }
    lastTap.current = now;
  };

  /* ── Bookmark ────────────────────────────────────────────────────── */
  const handleBookmark = useCallback(async () => {
    if (!currentUser) return;
    const next = !bookmarked;
    setBookmarked(next);
    try {
      if (!next) {
        const { error } = await supabase.from('bookmarks').delete().eq('user_id', currentUser.id).eq('post_id', post.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from('bookmarks').upsert({ user_id: currentUser.id, post_id: post.id });
        if (error) throw error;
      }
    } catch (_) { setBookmarked(!next); }
  }, [currentUser, post.id, bookmarked]);

  /* ── Follow ──────────────────────────────────────────────────────── */
  const handleFollow = useCallback(async () => {
    if (!currentUser || followLoading) return;
    setFollowLoading(true);
    const was = following;
    setFollowing(!was);
    closeMenu();
    try {
      const { error } = await supabase.rpc('handle_follow', {
        p_follower_id: currentUser.id, p_following_id: post.author_id,
        p_action: was ? 'unfollow' : 'follow',
      });
      if (error) throw error;
      if (!was) {
        supabase.from('notifications').upsert({
          recipient_id: post.author_id, type: 'follow',
          actor_username: currentUser.username, post_id: null,
          message: `${currentUser.username} started following you`, is_read: false,
        }, { onConflict: 'recipient_id,type,actor_username,post_id', ignoreDuplicates: true }).then(() => {});
      }
    } catch (_) { setFollowing(was); }
    finally { setFollowLoading(false); }
  }, [currentUser, post.author_id, following, followLoading]);

  /* ── Report ──────────────────────────────────────────────────────── */
  const handleReport = useCallback(async (reason) => {
    if (!currentUser || reported) return;
    closeMenu();
    const { error } = await supabase.from('reports').insert({
      reporter_id: currentUser.id, post_id: post.id, reason,
    });
    if (!error) setReported(true);
  }, [currentUser, post.id, reported]);

  /* ── Delete ──────────────────────────────────────────────────────── */
  const handleDelete = useCallback(async () => {
    if (!isOwner || deleting) return;
    if (!window.confirm('Delete this post?')) return;
    setDeleting(true); closeMenu();
    await supabase.from('posts').update({ is_deleted: true }).eq('id', post.id);
    onDelete?.(post.id);
  }, [isOwner, deleting, post.id, onDelete]);

  /* ── Copy link ───────────────────────────────────────────────────── */
  const handleCopyLink = useCallback(() => {
    navigator.clipboard.writeText(`${window.location.origin}/feed?post=${post.id}`).catch(() => {});
    setCopied(true); setTimeout(() => setCopied(false), 2000); closeMenu();
  }, [post.id]);

  /* ── Menu open / close ───────────────────────────────────────────── */
  const closeMenu = () => { setShowMenu(false); setMenuStep('main'); };

  const openMenu = () => {
    if (!isMobile && menuBtnRef.current) {
      const r = menuBtnRef.current.getBoundingClientRect();
      setMenuPos({ top: r.bottom + 8, right: window.innerWidth - r.right });
    }
    setMenuStep('main');
    setShowMenu(v => !v);
  };

  /* ── Render @mentions ────────────────────────────────────────────── */
  const renderContent = (text) =>
    text.split(/(@\w+)/g).map((part, i) =>
      part.startsWith('@')
        ? <span key={i} onClick={e => { e.stopPropagation(); onNavigateToProfile?.(part.slice(1)); }}
            style={{ color: 'var(--brand)', fontWeight: 700, cursor: 'pointer' }}>{part}</span>
        : part
    );

  /* ══════════════════════════════════════════════════════════════════
     MENU CONTENT — shared between desktop dropdown and mobile sheet
  ══════════════════════════════════════════════════════════════════ */
  const MenuItems = () => (
    menuStep === 'main' ? (
      <>
        <button className="pc-menu-item" onClick={handleCopyLink}>
          <span style={{ fontSize: '1.2rem' }}>🔗</span>
          {copied ? 'Copied!' : 'Copy link'}
        </button>
        <div className="pc-menu-sep" />

        {!isOwner && currentUser && (
          <>
            <button className="pc-menu-item" onClick={handleFollow} disabled={followLoading}>
              <span style={{ fontSize: '1.2rem' }}>{following ? '✖' : '➕'}</span>
              {followLoading ? 'Loading…' : following
                ? `Unfollow @${post.author_username}`
                : `Follow @${post.author_username}`}
            </button>
            <div className="pc-menu-sep" />
            {!reported
              ? <button className="pc-menu-item danger" onClick={() => setMenuStep('report')}>
                  <span style={{ fontSize: '1.2rem' }}>🚩</span> Report post
                </button>
              : <div style={{ padding: '12px 20px', fontSize: '0.85rem', color: 'var(--muted)', fontWeight: 600 }}>
                  ✓ Already reported
                </div>
            }
          </>
        )}

        {isOwner && (
          <>
            <button className="pc-menu-item danger" onClick={handleDelete}>
              <span style={{ fontSize: '1.2rem' }}>🗑️</span>
              {deleting ? 'Deleting…' : 'Delete post'}
            </button>
          </>
        )}

        {extraActions.length > 0 && (
          <>
            <div className="pc-menu-sep" />
            {extraActions.map((a, i) => (
              <button key={i} className="pc-menu-item" onClick={() => { a.onClick(); closeMenu(); }}>
                {a.label}
              </button>
            ))}
          </>
        )}
      </>
    ) : (
      /* Report reasons */
      <>
        <button className="pc-menu-item"
          onClick={() => setMenuStep('main')}
          style={{ color: 'var(--muted)', borderBottom: '1px solid var(--sand-light)' }}>
          ← Back
        </button>
        <div className="pc-menu-label">Why are you reporting?</div>
        {REPORT_REASONS.map(r => (
          <button key={r} className="pc-menu-item" onClick={() => handleReport(r)}>{r}</button>
        ))}
      </>
    )
  );

  /* ══════════════════════════════════════════════════════════════════
     PORTAL MENU — renders onto document.body, escapes ALL transforms
  ══════════════════════════════════════════════════════════════════ */
  const menuPortal = showMenu && typeof document !== 'undefined'
    ? createPortal(
        isMobile ? (
          /* ── Mobile: bottom action sheet ── */
          <>
            <div className="pc-sheet-overlay" onClick={closeMenu} />
            <div className="pc-action-sheet">
              <div className="pc-sheet-handle" />

              {/* Author info in sheet header */}
              <div className="pc-sheet-author">
                <div className="pc-sheet-author-avatar" style={{ background: avatarBg, color: avatarColor }}>
                  {post.author_username?.[0]?.toUpperCase()}
                </div>
                <div>
                  <div className="pc-sheet-author-name">@{post.author_username}</div>
                  <div className="pc-sheet-author-time">{formatTimeAgo(post.created_at)}</div>
                </div>
              </div>

              <MenuItems />

              <button className="pc-sheet-cancel" onClick={closeMenu}>Cancel</button>
            </div>
          </>
        ) : (
          /* ── Desktop: glassmorphism dropdown ── */
          <>
            <div className="pc-menu-overlay" onClick={closeMenu} />
            <div className="pc-menu" style={{ top: menuPos.top, right: menuPos.right }}>
              <MenuItems />
            </div>
          </>
        ),
        document.body
      )
    : null;

  /* ══════════════════════════════════════════════════════════════════
     RENDER
  ══════════════════════════════════════════════════════════════════ */
  return (
    <div className="pc-card">

      {/* ── HEADER ── */}
      <div className="pc-header">
        <div
          className="pc-avatar"
          onClick={() => onNavigateToProfile?.(post.author_username)}
          style={{ background: avatarBg, color: avatarColor }}
        >
          {post.author_username?.[0]?.toUpperCase()}
        </div>

        <div className="pc-user-info">
          <div className="pc-username-row">
            <span className="pc-username"
              onClick={() => onNavigateToProfile?.(post.author_username)}>
              {post.author_username}
            </span>
            {isOwner && <span className="pc-you-badge">you</span>}
          </div>
          <div className="pc-meta">
            {formatTimeAgo(post.created_at)}{post.edited_at && ' · edited'}
          </div>
        </div>

        <button ref={menuBtnRef} className="pc-more-btn" onClick={openMenu} aria-label="More options">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 8a2 2 0 110-4 2 2 0 010 4zm0 6a2 2 0 110-4 2 2 0 010 4zm0 6a2 2 0 110-4 2 2 0 010 4z"/>
          </svg>
        </button>
      </div>

      {/* ── TAG ── */}
      {post.tag && (
        <div className="pc-tag"
          style={{ background: `${tagInfo.color}18`, color: tagInfo.color, border: `1px solid ${tagInfo.color}30` }}>
          {tagInfo.emoji} {post.tag}
        </div>
      )}

      {/* ── BODY ── */}
      <div className="pc-body-container" onClick={handleDoubleTap}>
        <p className="pc-body">{renderContent(post.content)}</p>
        <svg
          className={`pc-big-heart ${bigHeartAnim ? 'animate' : ''}`}
          width="100" height="100" viewBox="0 0 24 24" fill="currentColor"
        >
          <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
        </svg>
      </div>

      {/* ── ACTION BAR ── */}
      <div className="pc-actions">
        {/* ❤ Heart */}
        <button
          className={`pc-pill ${isLiked ? 'liked' : ''}`}
          onClick={e => { e.stopPropagation(); handleVote(); }}
          disabled={!currentUser || voteLoading}
        >
          <svg className="pc-heart-icon" width="18" height="18" viewBox="0 0 24 24"
            fill={isLiked ? 'currentColor' : 'none'} stroke="currentColor"
            strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"/>
          </svg>
          <span>{upvotes}</span>
        </button>

        {/* 💬 Comment */}
        <button
          className={`pc-pill ${showComments ? 'comments-active' : ''}`}
          onClick={e => { e.stopPropagation(); setShowComments(v => !v); }}
        >
          <svg width="18" height="18" fill="none" stroke="currentColor"
            strokeWidth="2.2" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round"
              d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"/>
          </svg>
          <span>{commentCount}</span>
        </button>

        {/* 🔖 Bookmark */}
        <button
          className={`pc-pill ${bookmarked ? 'saved' : ''}`}
          onClick={e => { e.stopPropagation(); handleBookmark(); }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24"
            fill={bookmarked ? 'currentColor' : 'none'} stroke="currentColor"
            strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z"/>
          </svg>
        </button>

        {post.is_pinned && <span className="pc-pinned">📌 Pinned</span>}
      </div>

      {/* ── COMMENTS ── */}
      {showComments && (
        <div className="pc-comments-shell">
          <CommentSection
            postId={post.id}
            postAuthorId={post.author_id}
            currentUser={currentUser}
            onCountChange={setCommentCount}
          />
        </div>
      )}

      {/* ── PORTAL MENU ── */}
      {menuPortal}
    </div>
  );
};

export default PostCard;